import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@arcadeai/design-system/components/ui/atoms/command";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@arcadeai/design-system/components/ui/atoms/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@arcadeai/design-system/components/ui/atoms/select";
import { Separator } from "@arcadeai/design-system/components/ui/atoms/separator";
import { cn } from "@arcadeai/design-system/lib/utils";
import { Calendar, Check, Filter, Search } from "lucide-react";
import { useMemo } from "react";
import type { TimeRangeValue } from "./use-logs-source";

type LogLevel = "info" | "error" | "warning";

const LOG_LEVEL_OPTIONS: Array<{ label: string; value: LogLevel }> = [
  { label: "Info", value: "info" },
  { label: "Error", value: "error" },
  { label: "Warning", value: "warning" },
];

type LogsToolbarProps = {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  selectedLevels: LogLevel[];
  onLevelToggle: (value: LogLevel) => void;
  onResetFilters: () => void;
  isManaged: boolean;
  timeRange: TimeRangeValue;
  onTimeRangeChange: (value: TimeRangeValue) => void;
  timeRangeOptions: Array<{ label: string; value: TimeRangeValue }>;
};

export const LogsToolbar = ({
  searchQuery,
  onSearchChange,
  selectedLevels,
  onLevelToggle,
  onResetFilters,
  isManaged,
  timeRange,
  onTimeRangeChange,
  timeRangeOptions,
}: LogsToolbarProps) => {
  const selectedLevelsSet = useMemo(
    () => new Set(selectedLevels),
    [selectedLevels]
  );

  return (
    <div className="flex items-center gap-4">
      <div className="relative flex-1">
        <Search className="absolute top-3 left-3 size-4" />
        <Input
          className="h-10 w-full pl-10 text-md"
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search logs..."
          value={searchQuery}
        />
      </div>

      {!!isManaged && (
        <Select
          // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
          onValueChange={(value) => onTimeRangeChange(value as TimeRangeValue)}
          value={timeRange}
        >
          <SelectTrigger className="h-10 w-auto">
            <Calendar className="mr-1.5 size-4" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {timeRangeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <Popover>
        <PopoverTrigger
          render={
            <Button
              className="h-10 border-dashed"
              size="sm"
              variant="outline"
            />
          }
        >
          <Filter className="mr-2 size-4" />
          Filter
          {selectedLevelsSet.size > 0 && (
            <>
              <Separator className="mx-2 h-4" orientation="vertical" />
              <Badge
                className="rounded-sm px-1 font-normal lg:hidden"
                variant="secondary"
              >
                {selectedLevelsSet.size}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {selectedLevelsSet.size > 2 ? (
                  <Badge
                    className="rounded-sm px-1 font-normal"
                    variant="secondary"
                  >
                    {selectedLevelsSet.size} selected
                  </Badge>
                ) : (
                  Array.from(selectedLevelsSet).map((value) => (
                    <Badge
                      className="rounded-sm px-1 font-normal"
                      key={value}
                      variant="secondary"
                    >
                      {value.charAt(0).toUpperCase() + value.slice(1)}
                    </Badge>
                  ))
                )}
              </div>
            </>
          )}
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[200px] p-0">
          <Command>
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {LOG_LEVEL_OPTIONS.map((option) => {
                  const isSelected = selectedLevelsSet.has(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => onLevelToggle(option.value)}
                    >
                      <div
                        className={cn(
                          "mr-2 flex size-4 items-center justify-center rounded-sm border border-primary",
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50 [&_svg]:invisible"
                        )}
                      >
                        <Check className="size-4" />
                      </div>
                      <span>{option.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              {selectedLevelsSet.size > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      className="justify-center text-center"
                      onSelect={onResetFilters}
                    >
                      Reset filters
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
